import React, { useEffect, useState } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import AdsSettings from "./components/AdsSettings";
import DeepLinkSettings from "./components/DeepLinkSettings";
import PasswordInput from "./components/PasswordInput";
import toast, { Toaster } from "react-hot-toast";
import { FiSettings, FiPlus, FiEdit2, FiX, FiSave, FiKey } from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const emptyForm = { year: "", coin: "", baseRate: "", refallRate: "" };

const ChangePasswordTab = () => {
  const token = localStorage.getItem("token");
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${baseUrl}/auth/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: form.currentPassword, newPassword: form.newPassword }),
      });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Failed to change password");
      toast.success("Password changed successfully. Your other sessions have been logged out.");
      setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-md">
      <h3 className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
        <FiKey size={14} className="text-purple-500" /> Change Password
      </h3>
      <p className="text-xs text-gray-400 mb-5">Changing your password logs you out of every other active session.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Current Password</label>
          <PasswordInput required value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">New Password</label>
          <PasswordInput required minLength={8} value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Confirm New Password</label>
          <PasswordInput required minLength={8} value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} />
        </div>
        <button type="submit" disabled={submitting}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
          <FiSave size={14} /> {submitting ? "Saving..." : "Change Password"}
        </button>
      </form>
    </div>
  );
};

const Field = ({ label, name, value, onChange, step }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
    <input
      type="number"
      name={name}
      placeholder={label}
      value={value}
      onChange={onChange}
      step={step}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
    />
  </div>
);

const TABS = [
  { key: "coin-supply", label: "Coin Supply" },
  { key: "ads-settings", label: "Ads Settings" },
  { key: "deep-link", label: "Deep Link" },
  { key: "my-account", label: "My Account" },
];

const Settings = () => {
  const [activeTab, setActiveTab]        = useState("coin-supply");
  const [data, setData]                  = useState([]);
  const [loading, setLoading]            = useState(true);
  const [error, setError]                = useState("");
  const [editingIndex, setEditingIndex]  = useState(null);
  const [submitting, setSubmitting]      = useState(false);
  const [formData, setFormData]          = useState(emptyForm);

  const token = localStorage.getItem("token");

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = async () => {
    setLoading(true);
    try {
      const res    = await fetch(`${baseUrl}/setting/yearly-total-supply`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status && result.data) setData(result.data);
      else throw new Error(result.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData(data[index]);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => { setEditingIndex(null); setFormData(emptyForm); };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const current  = data[editingIndex];
      const supplyId = current._id || current.id;
      const res      = await fetch(`${baseUrl}/setting/yearly-total-supply/${supplyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      const updated = [...data];
      updated[editingIndex] = formData;
      setData(updated);
      setEditingIndex(null);
      setFormData(emptyForm);
      toast.success("Supply updated successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNew = async () => {
    if (!formData.year || !formData.coin || !formData.baseRate || !formData.refallRate) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      const res    = await fetch(`${baseUrl}/setting/yearly-total-supply`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Add failed");

      setData([...data, result.data]);
      setFormData(emptyForm);
      toast.success("New supply added successfully!");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isEditing = editingIndex !== null;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <FiSettings size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Settings</h2>
              <p className="text-sm text-gray-400 mt-0.5">Manage app configuration</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-white border border-gray-100 rounded-xl p-1 w-fit shadow-sm">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2 text-sm font-medium rounded-lg transition ${
                  activeTab === tab.key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Coin Supply Tab */}
          {activeTab === "coin-supply" && (
            <>
              {/* Form card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
                  {isEditing
                    ? <><FiEdit2 size={14} className="text-amber-500" /> Edit Supply — Year {data[editingIndex]?.year}</>
                    : <><FiPlus size={14} className="text-blue-500" /> Add New Supply</>
                  }
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  <Field label="Year"          name="year"       value={formData.year}       onChange={handleChange} />
                  <Field label="Total Coins"   name="coin"       value={formData.coin}       onChange={handleChange} />
                  <Field label="Base Rate"     name="baseRate"   value={formData.baseRate}   onChange={handleChange} step="0.01" />
                  <Field label="Referral Rate" name="refallRate" value={formData.refallRate} onChange={handleChange} step="0.01" />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={isEditing ? handleSave : handleAddNew}
                    disabled={submitting}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition"
                  >
                    {isEditing ? <FiSave size={14} /> : <FiPlus size={14} />}
                    {submitting ? "Saving..." : isEditing ? "Save Changes" : "Add Supply"}
                  </button>
                  {isEditing && (
                    <button
                      onClick={handleCancel}
                      className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
                    >
                      <FiX size={14} /> Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* Table card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Yearly Supply Records</h3>
                  {data.length > 0 && (
                    <span className="text-xs text-gray-400">{data.length} record{data.length !== 1 ? "s" : ""}</span>
                  )}
                </div>

                {loading ? (
                  <div className="flex justify-center items-center py-16">
                    <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : error ? (
                  <div className="m-5 bg-red-50 border border-red-200 text-red-600 rounded-xl p-4 text-sm">{error}</div>
                ) : data.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <FiSettings size={32} className="text-gray-300 mb-3" />
                    <p className="text-sm">No supply records yet. Add one above.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                          <th className="px-5 py-3">#</th>
                          <th className="px-5 py-3">Year</th>
                          <th className="px-5 py-3 text-right">Total Coins</th>
                          <th className="px-5 py-3 text-right">Base Rate</th>
                          <th className="px-5 py-3 text-right">Referral Rate</th>
                          <th className="px-5 py-3 text-center w-24">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {data.map((item, index) => (
                          <tr
                            key={index}
                            className={`transition ${editingIndex === index ? "bg-amber-50" : "hover:bg-gray-50"}`}
                          >
                            <td className="px-5 py-3.5 text-gray-400 text-xs">{index + 1}</td>
                            <td className="px-5 py-3.5">
                              <span className="inline-block bg-indigo-50 text-indigo-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">
                                {item.year}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-right font-semibold text-gray-800">
                              {Number(item.coin).toLocaleString()}
                            </td>
                            <td className="px-5 py-3.5 text-right text-gray-600">{item.baseRate}</td>
                            <td className="px-5 py-3.5 text-right text-gray-600">{item.refallRate}</td>
                            <td className="px-5 py-3.5 text-center">
                              <button
                                onClick={() => handleEdit(index)}
                                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg transition mx-auto ${
                                  editingIndex === index
                                    ? "bg-amber-100 text-amber-600"
                                    : "bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600"
                                }`}
                              >
                                <FiEdit2 size={12} />
                                {editingIndex === index ? "Editing" : "Edit"}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Ads Settings Tab */}
          {activeTab === "ads-settings" && <AdsSettings />}

          {/* Deep Link Tab */}
          {activeTab === "deep-link" && <DeepLinkSettings />}

          {/* My Account Tab */}
          {activeTab === "my-account" && <ChangePasswordTab />}

        </div>
      </div>
    </div>
  );
};

export default Settings;
